/**
 * Timer — countdown with 3-beep pattern + vibrate on complete (bonus H).
 *
 * AudioContext is created on user gesture (Start Timer click) to comply
 * with Chrome autoplay policy. Uses setInterval with per-second re-render.
 */
import { useState, useEffect, useRef, useCallback } from 'react';

export interface TimerProps {
  duration: number; // seconds
  onComplete(): void;
}

export function Timer({ duration, onComplete }: TimerProps) {
  const [timeLeft, setTimeLeft] = useState(duration);
  const completedRef = useRef(false);

  const playBeeps = useCallback(() => {
    try {
      const ctx = new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext)();
      const beeps = [200, 100, 200, 100, 200]; // on/off ms
      let offset = ctx.currentTime;
      for (const dur of beeps) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, offset);
        gain.gain.setValueAtTime(0.3, offset);
        gain.gain.exponentialRampToValueAtTime(0.001, offset + dur / 1000);
        osc.start(offset);
        osc.stop(offset + dur / 1000);
        offset += dur / 1000;
      }
      ctx.close();
    } catch {
      // audio not available
    }
  }, []);

  useEffect(() => {
    if (completedRef.current) return;

    if (timeLeft <= 0) {
      completedRef.current = true;
      playBeeps();
      try {
        navigator.vibrate?.([200, 100, 200, 100, 200]);
      } catch {
        // vibrate not supported
      }
      onComplete();
      return;
    }

    const id = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(id);
  }, [timeLeft, onComplete, playBeeps]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="my-3 p-4 bg-yellow-500/10 border-l-4 border-yellow-500 rounded-r-md">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <svg
            aria-hidden="true"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-yellow-400 mr-3"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span className="font-bold text-yellow-300 text-2xl tracking-wider">
            {String(minutes).padStart(2, '0')}:
            {String(seconds).padStart(2, '0')}
          </span>
        </div>
        <span className="text-sm font-medium text-yellow-400">
          Timer Running...
        </span>
      </div>
    </div>
  );
}
